import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { X, Flame } from "lucide-react";
import { useAuth } from "./AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SEEN_KEY = "domigrill_launch_seen";

/**
 * Shows a one-time launch banner + full-screen confetti shower the very first
 * time a manager visits the Dashboard after an admin presses "Launch Now".
 * Detection: compare server-side `launched_at` ISO against the value stored in
 * localStorage. If different (or missing), fire the celebration. Dismiss
 * stamps the current value so the banner won't re-appear for this browser.
 */
export default function DomiGrillLaunchBanner() {
  const { isSuperAdmin } = useAuth();
  const [launchedAt, setLaunchedAt] = useState(null);
  const [seasonStart, setSeasonStart] = useState(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (!isSuperAdmin) return; // Domi-Grill is now Super Admin only — banner only fires for them
    let cancelled = false;
    const triggerFromConfig = (data) => {
      const la = data?.launched_at || null;
      const ss = data?.season_start || null;
      if (!la) return;
      const seen = localStorage.getItem(SEEN_KEY);
      if (seen === la) return;
      setLaunchedAt(la);
      setSeasonStart(ss);
      setVisible(true);
      fireShower();
    };

    axios.get(`${API}/domigrill/config`)
      .then((res) => { if (!cancelled) triggerFromConfig(res.data); })
      .catch(() => { /* silently ignore — banner is non-critical */ });

    // Real-time hook: admin pressed "Launch Now" elsewhere on the same page —
    // show the banner instantly without a refresh.
    const onLaunched = (e) => triggerFromConfig(e.detail || {});
    window.addEventListener("domigrill:launched", onLaunched);
    return () => {
      cancelled = true;
      window.removeEventListener("domigrill:launched", onLaunched);
    };
  }, [isSuperAdmin]);

  const fireShower = async () => {
    try {
      const confetti = (await import("canvas-confetti")).default;
      const colours = ["#E31837", "#0071e3", "#FFB300", "#16a34a", "#8b5cf6", "#FFD700", "#fff"];
      const bursts = [
        { x: 0.1, y: 0.3, t: 0 },
        { x: 0.5, y: 0.2, t: 120 },
        { x: 0.9, y: 0.3, t: 240 },
        { x: 0.3, y: 0.5, t: 380 },
        { x: 0.7, y: 0.5, t: 500 },
        { x: 0.5, y: 0.8, t: 650 },
      ];
      bursts.forEach(({ x, y, t }) => {
        setTimeout(() => {
          confetti({
            particleCount: 90,
            spread: 100,
            startVelocity: 55,
            origin: { x, y },
            colors: colours,
            scalar: 1.2,
            zIndex: 9999,
          });
        }, t);
      });
    } catch (e) {
      console.warn("[DomiGrillLaunchBanner] confetti unavailable", e);
    }
  };

  const dismiss = () => {
    if (launchedAt) localStorage.setItem(SEEN_KEY, launchedAt);
    setVisible(false);
  };

  if (!visible || !isSuperAdmin) return null;

  const startsLabel = seasonStart
    ? new Date(seasonStart).toLocaleDateString(undefined, { weekday: "long", day: "numeric", month: "long" })
    : "soon";

  return (
    <div
      data-testid="domigrill-launch-banner"
      className="relative mb-6 overflow-hidden rounded-sm border-2 border-[#FFB300] shadow-lg"
      style={{
        background: "linear-gradient(135deg, #7a0d1f 0%, #E31837 45%, #FFB300 100%)",
        fontFamily: "Inter, sans-serif",
      }}
    >
      <div className="absolute inset-0 pointer-events-none opacity-20"
           style={{ backgroundImage: "radial-gradient(circle at 20% 30%, #fff 1px, transparent 1px), radial-gradient(circle at 80% 70%, #fff 1px, transparent 1px)", backgroundSize: "40px 40px" }} />
      <div className="relative flex items-center gap-4 px-5 py-4 text-white">
        <div className="hidden sm:flex items-center justify-center w-14 h-14 rounded-full bg-white/20 backdrop-blur shrink-0 animate-pulse">
          <Flame size={32} className="text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[0.3em] font-bold opacity-80">It's lit · Domi-Grill</div>
          <div className="text-xl sm:text-2xl font-black uppercase tracking-wide leading-tight">
            The season has launched!
          </div>
          <div className="text-sm sm:text-base font-medium opacity-95 mt-0.5">
            Streaks start <span className="font-bold underline decoration-[#FFD700] decoration-2 underline-offset-2">{startsLabel}</span> — fire up the grill and chase your first win.
          </div>
        </div>
        <Link
          to="/domi-grill"
          data-testid="domigrill-launch-cta"
          onClick={dismiss}
          className="hidden sm:inline-flex shrink-0 items-center gap-2 px-4 py-2 bg-white text-[#E31837] hover:bg-[#FFD700] hover:text-black transition-colors rounded-sm text-sm font-black uppercase tracking-[0.15em]"
        >
          Visit The Race →
        </Link>
        <button
          onClick={dismiss}
          data-testid="domigrill-launch-dismiss"
          aria-label="Dismiss launch banner"
          className="shrink-0 w-8 h-8 rounded-full hover:bg-white/20 flex items-center justify-center transition-colors"
        >
          <X size={18} />
        </button>
      </div>
      <Link
        to="/domi-grill"
        onClick={dismiss}
        className="sm:hidden block bg-black/30 hover:bg-black/40 transition-colors text-white text-center py-2 text-xs font-black uppercase tracking-[0.2em]"
      >
        Visit The Race →
      </Link>
    </div>
  );
}
